export const queryKeys = {
  auth: {
    all: ['auth'] as const,
    user: () => ['auth', 'user'] as const,
    // The signed-in user's own `public.users` row. Distinct from
    // `profiles.detail` (the public, role-enriched profile blob) so the two
    // never clobber each other's cache entry for the same user id.
    profile: (userId: string) => ['auth', 'profile', userId] as const,
  },

  workspaces: {
    all: ['workspaces'] as const,
    list: () => ['workspaces', 'list'] as const,
    detail: (slug: string) => ['workspaces', 'detail', slug] as const,
    stats: (id: string) => ['workspaces', 'stats', id] as const,
    active: (id: string) => ['workspaces', 'active', id] as const,
    writers: (id: string) => ['workspaces', 'writers', id] as const,
    // Flat `WorkspaceWriter[]` (server-action parity shape) used by the writers
    // list / kanban / settings views — distinct cache entry from `writers`
    // (the nested member shape consumed by task-detail).
    writersList: (id: string) => ['workspaces', 'writers-list', id] as const,
    /** Owner/employer members of a workspace (WS-10). */
    employersList: (id: string) =>
      ['workspaces', 'employers-list', id] as const,
    writerDetail: (workspaceId: string, userId: string) =>
      ['workspaces', 'writer-detail', workspaceId, userId] as const,
    ownerName: (ownerId: string) =>
      ['workspaces', 'owner-name', ownerId] as const,
  },

  tasks: {
    all: ['tasks'] as const,
    list: (workspaceId: string, filters?: Record<string, unknown>) =>
      ['tasks', 'list', workspaceId, filters ?? {}] as const,
    writerList: (filters?: Record<string, unknown>) =>
      ['tasks', 'writer-list', filters ?? {}] as const,
    detail: (id: string) => ['tasks', 'detail', id] as const,
    comments: (submissionId: string) =>
      ['tasks', 'comments', submissionId] as const,
    submissionComments: (
      submissionId: string,
      includeResolved?: boolean,
    ) =>
      [
        'tasks',
        'submission-comments',
        submissionId,
        includeResolved ?? false,
      ] as const,
    commentsAcrossVersions: (taskId: string) =>
      ['tasks', 'comments-across-versions', taskId] as const,
  },

  notifications: {
    all: ['notifications'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['notifications', 'list', filters ?? {}] as const,
    unreadCount: () => ['notifications', 'unread-count'] as const,
    preferences: () => ['notifications', 'preferences'] as const,
  },

  profiles: {
    all: ['profiles'] as const,
    detail: (userId: string) => ['profiles', 'detail', userId] as const,
    // Pages accumulate inside one infinite query (MKT-09), so the key is no
    // longer per-page.
    ratings: (userId: string) => ['profiles', 'ratings', userId] as const,
    workspaceCount: (userId: string) =>
      ['profiles', 'workspace-count', userId] as const,
  },

  wallet: {
    all: ['wallet'] as const,
    workspace: (id: string) => ['wallet', 'workspace', id] as const,
    writer: () => ['wallet', 'writer'] as const,
    transactions: (walletId: string, filters?: Record<string, unknown>) =>
      ['wallet', 'transactions', walletId, filters ?? {}] as const,
  },

  subscription: {
    /** The current user's account-level subscription. */
    account: () => ['subscription', 'account'] as const,
    plans: () => ['subscription', 'plans'] as const,
    /** First-time-trial eligibility (Stripe-native trial at Checkout). */
    trialEligibility: () => ['subscription', 'trial-eligibility'] as const,
    /** Applied coupons / promotion codes (live Stripe read, no DB mirror). */
    discounts: () => ['subscription', 'discounts'] as const,
  },

  ratings: {
    all: ['ratings'] as const,
    pending: () => ['ratings', 'pending'] as const,
    taskStatus: (taskId: string) =>
      ['ratings', 'task-status', taskId] as const,
  },

  aiCheck: {
    results: (submissionId: string, submissionType: string) =>
      ['ai-check', submissionId, submissionType] as const,
  },

  invitations: {
    all: ['invitations'] as const,
    byToken: (token: string) => ['invitations', 'token', token] as const,
  },

  taskTemplates: {
    all: ['taskTemplates'] as const,
    list: (workspaceId: string) =>
      ['taskTemplates', 'list', workspaceId] as const,
    detail: (templateId: string) =>
      ['taskTemplates', 'detail', templateId] as const,
  },

  workspaceContentTypes: {
    all: ['workspaceContentTypes'] as const,
    list: (workspaceId: string) =>
      ['workspaceContentTypes', 'list', workspaceId] as const,
  },

  featureFlags: {
    all: ['feature-flags'] as const,
  },

  dashboard: {
    all: ['dashboard'] as const,
    pipeline: () => ['dashboard', 'pipeline'] as const,
    earnings: () => ['dashboard', 'earnings'] as const,
    metrics: () => ['dashboard', 'metrics'] as const,
    alerts: () => ['dashboard', 'alerts'] as const,
    payoutProfile: () => ['dashboard', 'payout-profile'] as const,
    snapshot: (workspaceId: string) =>
      ['dashboard', 'snapshot', workspaceId] as const,
  },

  // Workspace-scoped writer home (PRD §5.13.1) — CSR replacement for the
  // server `getWriterDashboardSummary`/`getWriterAttentionItems`/
  // `getWriterRecentActivity` actions.
  writerHome: {
    summary: (workspaceId: string) =>
      ['writer-home', 'summary', workspaceId] as const,
    attention: (workspaceId: string) =>
      ['writer-home', 'attention', workspaceId] as const,
    activity: (workspaceId: string) =>
      ['writer-home', 'activity', workspaceId] as const,
  },

  writerProfile: {
    // The signed-in user's own writer_profiles row.
    self: (userId: string) => ['writer-profile', 'self', userId] as const,
  },

} as const;
